/**
 * postLoginReturn: brings a signed-out visitor back to the page they opened after
 * they sign in.
 *
 * PRP-MCP-1 stored only the MCP consent screen; PRP-MCP-8 added the exact trial link.
 * PRP-MCP-11 G3 (founder decision F2): ANY protected link survives sign-in, e.g. the
 * admin link in a founder email (`/admin/users/<id>`), a review page, an estimate page.
 *
 * The path travels two ways, so either one alone is enough:
 *  - a first-party cookie, 30 minutes, SameSite=Lax: survives the Google redirect, a
 *    popup, the auth handler's fallback, and a new tab. On https it is
 *    `__Host-aiea_return` (Secure, Path=/, no Domain), so a sibling *.anterotrail.com
 *    cannot plant or pin it; a plain `aiea_return` is ignored there. Plain
 *    `aiea_return` is used only on http (localhost dev), where `__Host-` is impossible;
 *  - `?next=` on the `/login` URL: survives cookie loss, and the Google redirect
 *    returns to the exact /login URL it left from.
 *
 * Open-redirect safety (security review L1): only a same-origin relative path is ever
 * stored or returned, and always in its NORMALISED form (`URL` resolves `.`/`..`).
 * It must start with exactly one `/`; `//`, `/\`, backslashes, control characters,
 * schemes, anything resolving to another origin, and any path whose normalised or
 * percent-decoded form starts with `//` are rejected. `/login`, `/signup` and the auth
 * helper paths under `/__/` are rejected after percent-decoding each segment, because
 * react-router decodes `/%6cogin` to `/login` before matching.
 */

/** Cookie name on http (localhost dev) only. */
export const RETURN_COOKIE = 'aiea_return';
/** Cookie name on https: the `__Host-` prefix forces Secure, Path=/ and no Domain. */
export const HOST_RETURN_COOKIE = '__Host-aiea_return';
/** Pre-PRP-MCP-11 localStorage key; only cleared now. */
const LEGACY_KEY = 'aiea_post_login_return';

const MAX_AGE_MS = 30 * 60 * 1000;
/** Consent requests are short-lived (PRP-MCP-1); keep their 15 minutes. */
const MCP_MAX_AGE_MS = 15 * 60 * 1000;
/** A stored timestamp may be this far in the future (clock skew), no more. */
const FUTURE_SKEW_MS = 60 * 1000;
const MCP_PREFIX = '/mcp/authorize';
const MAX_LEN = 2000;
const FALLBACK_ORIGIN = 'https://aiea-return.invalid';

function currentOrigin(): string {
  try {
    const o = typeof location !== 'undefined' ? location.origin : '';
    return o && o !== 'null' ? o : FALLBACK_ORIGIN;
  } catch {
    return FALLBACK_ORIGIN;
  }
}

function isHttps(): boolean {
  try {
    return typeof location !== 'undefined' && location.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * The normalised same-origin path (`pathname + search + hash`) for `path`, or null when
 * it is not one we may send a user to after sign-in.
 */
export function normaliseReturnPath(path: unknown): string | null {
  if (typeof path !== 'string' || path.length === 0 || path.length > MAX_LEN) return null;
  if (path[0] !== '/' || path[1] === '/' || path[1] === '\\') return null;
  // Backslashes act as slashes and tabs/newlines are stripped by URL parsers: reject both.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return null;
  const origin = currentOrigin();
  let url: URL;
  try {
    url = new URL(path, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const { pathname } = url;
  if (pathname.startsWith('//') || pathname.includes('\\')) return null;
  // What the router will match: each segment percent-decoded.
  let decoded: string;
  try {
    decoded = pathname.split('/').map(decodeURIComponent).join('/').toLowerCase();
  } catch {
    return null; // malformed percent-encoding
  }
  if (decoded.startsWith('//') || decoded.includes('\\')) return null;
  const blocked = ['/login', '/signup'].some((b) => decoded === b || decoded.startsWith(`${b}/`));
  if (blocked || decoded === '/__' || decoded.startsWith('/__/')) return null;
  const out = pathname + url.search + url.hash;
  return out.length <= MAX_LEN ? out : null;
}

/** True when `path` is a same-origin relative path we may send a user to after sign-in. */
export function isSafeReturnPath(path: unknown): path is string {
  return normaliseReturnPath(path) !== null;
}

function maxAgeFor(path: string): number {
  return path.startsWith(MCP_PREFIX) ? MCP_MAX_AGE_MS : MAX_AGE_MS;
}

function cookieName(): string {
  return isHttps() ? HOST_RETURN_COOKIE : RETURN_COOKIE;
}

function writeCookie(value: string, maxAgeSec: number): void {
  try {
    const secure = isHttps() ? '; Secure' : '';
    document.cookie = `${cookieName()}=${value}; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
  } catch {
    /* cookies disabled: ?next= still carries the path */
  }
}

function readCookieRaw(): string | null {
  const name = cookieName();
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
    return hit ? hit.slice(name.length + 1) : null;
  } catch {
    return null;
  }
}

/** The `next` query value on a /login or /signup URL, normalised, when it is safe. */
export function nextFromSearch(search: string | undefined | null): string | null {
  if (!search) return null;
  try {
    return normaliseReturnPath(new URLSearchParams(search).get('next'));
  } catch {
    return null;
  }
}

/** The /login URL for a signed-out visitor who opened `path`. */
export function loginUrlFor(path: string): string {
  const p = normaliseReturnPath(path);
  return p && p !== '/' ? `/login?next=${encodeURIComponent(p)}` : '/login';
}

/**
 * Remembers `path` (pathname + search + hash) for after sign-in. An unsafe path is
 * ignored; `/` clears any older stored path (the dashboard is the default anyway).
 */
export function stashReturnPath(path: string): void {
  if (path === '/') {
    clearReturnPath();
    return;
  }
  const p = normaliseReturnPath(path);
  if (!p) return;
  const value = encodeURIComponent(JSON.stringify({ p, t: Date.now() }));
  writeCookie(value, Math.round(maxAgeFor(p) / 1000));
}

/** The stored cookie path, or null when absent, expired, stamped in the future, or unsafe. */
function readStored(): string | null {
  const raw = readCookieRaw();
  if (!raw) return null;
  try {
    const { p, t } = JSON.parse(decodeURIComponent(raw)) as { p?: unknown; t?: unknown };
    const path = normaliseReturnPath(p);
    const now = Date.now();
    if (path && typeof t === 'number' && t <= now + FUTURE_SKEW_MS && now - t <= maxAgeFor(path)) return path;
  } catch {
    /* fall through: a malformed cookie is dropped */
  }
  clearReturnPath();
  return null;
}

/**
 * Where to go after sign-in: `?next=` on `search` first, then the cookie. Null when
 * neither holds a safe, unexpired path (the caller then goes to the dashboard).
 */
export function readReturnPath(search?: string | null): string | null {
  return nextFromSearch(search) ?? readStored();
}

/** Clears the stored path (after landing, or when the consent screen has loaded). */
export function clearReturnPath(): void {
  writeCookie('', 0);
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

/** True when a return path cookie is present (cheap check before clearing). */
export function hasStoredReturnPath(): boolean {
  return readCookieRaw() !== null;
}
